import { Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactElement, useState } from 'react';
import { ICandidate } from 'wisestep-api';
import {
  ChipsType,
  WsButton,
  WsTagsList,
  EmailList,
  WsTypoVariants,
  WsColors,
  WsTypography,
} from 'wisestep-base';

import { success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { useCandidateViewData } from '../../../utils/CandidateViewContext';
import CardWithAccordion from '../CardWithAccordion';

import { copyStringToClipboard } from './Helper';
import TextWithLabel from './TextWithLabel';

export interface SplicedData {
  [key: number]: {
    initial: CandidateDetails[];
    hidden: CandidateDetails[];
  };
}
export interface CandidateDetails {
  title: string;
  value: string | string[];
  type?: 'BUTTON' | 'TEXT' | 'ICON' | 'EMAIL';
  icon?: ReactElement;
  isNotAvailable?: boolean;
}
export interface CandidateDetailsCardProps {
  title: string;
  details?: SplicedData;
  skillDetails?: string[];
  isSkills?: boolean;
  showMissingInfo?: boolean;
  isProfile?: boolean;
  candidate?: ICandidate;
}

const CandidateDetailsCard = ({
  title,
  details = [],
  isSkills = false,
  skillDetails = [],
  isProfile = true,
  candidate,
}: CandidateDetailsCardProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [sizesData, setSizesData] = useState<(string | number)[]>([]);

  document.addEventListener('layout-resize', ((event: CustomEvent) => {
    setSizesData(event.detail);
  }) as EventListener);

  const handleProfileAction = async (action: string, value: string | null) => {
    if (action === 'copy' && value) {
      copyStringToClipboard(value);
      setNotification(success('Copied Successfully !!'));
    }
  };
  const getDisplayValue = (
    details: CandidateDetails
  ): string | number | ReactElement[] | ReactElement | string[] => {
    switch (details.type) {
      case 'EMAIL': {
        if (typeof details.value === 'string') {
          return details.value === 'NA' ? (
            details.value
          ) : (
            <EmailList emails={[details.value]} handleProfileAction={handleProfileAction} />
          );
        } else {
          return <EmailList emails={details.value} handleProfileAction={handleProfileAction} />;
        }
      }
      case 'BUTTON': {
        if (typeof details.value === 'string') {
          return details.value === 'NA' ? (
            details.value
          ) : (
            <WsButton variant="outlined" fullWidth={false}>
              {details.value ?? 'text'}
            </WsButton>
          );
        } else {
          return details.value.map((data, index) => (
            <WsButton key={index} variant="outlined" fullWidth={false}>
              {data}
            </WsButton>
          ));
        }
      }
      case 'ICON': {
        if (typeof details.value === 'string') {
          return details.value === 'NA' ? (
            details.value
          ) : (
            <WsButton variant="outlined" fullWidth={false}>
              {details.value ?? 'text'}
            </WsButton>
          );
        } else {
          return details.value.map((data, index) => (
            <WsButton key={index} variant="outlined" fullWidth={false}>
              {data}
            </WsButton>
          ));
        }
      }
      default: {
        return details.value;
      }
    }
  };

  const getInitialSize = (sizes: (string | number)[]): number => {
    if (sizes?.length > 0) {
      const leftPaneSize = isProfile ? (sizes[0] as number) : (sizes[1] as number);
      if (leftPaneSize > 800) {
        return 5;
      } else if (leftPaneSize < 800 && leftPaneSize > 500) {
        return 4;
      } else if (leftPaneSize < 350) {
        return 2;
      }
    }
    return 3;
  };
  const getGridSize = (sizes: (string | number)[]): number => {
    if (sizes?.length > 0) {
      const leftPaneSize = isProfile ? (sizes[0] as number) : (sizes[1] as number);
      if (leftPaneSize > 800) {
        return 2.4;
      } else if (leftPaneSize < 800 && leftPaneSize > 500) {
        return 3;
      } else if (leftPaneSize < 350) {
        return 6;
      }
    }
    return 4;
  };
  const { candidateViewData } = useCandidateViewData();
  const isInDnc =
    (candidateViewData?.dnc?.isDeleted ?? false) || (candidateViewData?.dnc?.isInDnc ?? false);

  return (
    <CardWithAccordion
      title={title}
      topChild={
        <>
          {isInDnc && (
            <Grid xs={12} ml={7.5}>
              <WsTypography>No Data To Show</WsTypography>
            </Grid>
          )}
          {!isInDnc &&
          details[getInitialSize(sizesData)]?.initial.length === 0 &&
          skillDetails?.length === 0 ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              display="flex"
              justifyContent="start"
              justifySelf="center"
              mt={1}
              px={2}
              py={1.5}
            >
              {/* <WsTypography color={WsTypoColors.SECONDARY}>No data to show</WsTypography> */}
            </Grid>
          ) : (
            <>
              {isSkills ? (
                <WsTagsList
                  chipArray={skillDetails?.slice(0, getInitialSize(sizesData)) ?? []}
                  count={getInitialSize(sizesData)}
                  type={ChipsType.PRIMARY}
                  borderColorFlag={true}
                ></WsTagsList>
              ) : (
                <>
                  {!isInDnc &&
                    details[getInitialSize(sizesData)].initial
                      .filter(
                        (i) =>
                          (typeof i.value === 'string' && i.value.trim() !== '') ||
                          (Array.isArray(i.value) && i.value.length > 0)
                      )
                      .map((detailsObj, index) => (
                        <Grid item xs={getGridSize(sizesData)} key={index} mt={1} mb={1}>
                          <TextWithLabel
                            valueVariant={WsTypoVariants.BODY1}
                            labelColor={WsColors.TertiaryShades900}
                            value={getDisplayValue(detailsObj)}
                            label={detailsObj.title ?? 'Title'}
                            candidate={candidate}
                            isProfile={isProfile}
                          />
                        </Grid>
                      ))}
                </>
              )}
            </>
          )}
        </>
      }
      bottomChild={
        <>
          {!isInDnc && (
            <>
              {isSkills ? (
                <WsTagsList
                  chipArray={skillDetails?.slice(getInitialSize(sizesData)) ?? []}
                  count={skillDetails?.slice(getInitialSize(sizesData)).length}
                  type={ChipsType.PRIMARY}
                  borderColorFlag={true}
                ></WsTagsList>
              ) : (
                <>
                  {details[getInitialSize(sizesData)].hidden.map((detailsObj, index) => (
                    <Grid item xs={getGridSize(sizesData)} key={index} mt={1} mb={1}>
                      <TextWithLabel
                        valueVariant={WsTypoVariants.BODY1}
                        value={getDisplayValue(detailsObj)}
                        label={detailsObj.title ?? 'Title'}
                        labelColor={WsColors.TertiaryShades900}
                        candidate={candidate}
                        isProfile={isProfile}
                      />
                    </Grid>
                  ))}
                </>
              )}
            </>
          )}
        </>
      }
      hasData={
        !isEmpty(details[getInitialSize(sizesData)]?.hidden) ||
        !isEmpty(
          details[getInitialSize(sizesData)]?.initial.filter(
            (i) =>
              (typeof i.value === 'string' && i.value.trim() !== '') ||
              (Array.isArray(i.value) && i.value.length > 0)
          )
        ) ||
        !isEmpty(skillDetails)
      }
    ></CardWithAccordion>
  );
};

export default CandidateDetailsCard;
